import styled from "@emotion/styled";
import { Table, TableWrapper, Badge, Flex } from "../../components/ui";
import { HexDisplay } from "../../components/HexDisplay";
import { theme } from "../../lib/theme";
import type { PublicAddress } from "../../lib/api";
import { TableCard, SectionHeading } from "./styled";

const RoleBadge = styled(Badge)`
  font-size: 9px;
  padding: 1px 5px;
`;

function roleColor(role: string): string {
  switch (role) {
    case "callee": return theme.colors.success;
    case "msgSender": return theme.colors.primary;
    case "feePayer": return theme.colors.warning;
    case "calldata": return theme.colors.accent;
    default: return theme.colors.textMuted;
  }
}

function phaseColor(phase: string | null): string {
  if (phase === "setup") return theme.colors.accent;
  if (phase === "teardown") return theme.colors.warning;
  return theme.colors.primary;
}

export function PublicAddressesSection({
  addresses,
}: {
  addresses: PublicAddress[];
  feePayer?: string;
}) {
  if (addresses.length === 0) return null;

  return (
    <>
      <SectionHeading>
        Visible Addresses ({addresses.length})
      </SectionHeading>
      <TableCard style={{ marginBottom: theme.spacing.md }}>
        <TableWrapper>
          <Table>
            <thead>
              <tr>
                <th>Address</th>
                <th>Label / Type</th>
                <th>Appearances</th>
              </tr>
            </thead>
            <tbody>
              {addresses.map((a, i) => (
                <tr key={i}>
                  <td>
                    <HexDisplay address={a.address} />
                  </td>
                  <td>
                    <Flex gap="4px" wrap>
                      {a.label && <Badge>{a.label}</Badge>}
                      {a.contractType && (
                        <Badge color={theme.colors.textMuted}>{a.contractType}</Badge>
                      )}
                      {!a.label && !a.contractType && (
                        <span style={{ color: theme.colors.textMuted }}>—</span>
                      )}
                    </Flex>
                  </td>
                  <td>
                    <Flex gap="4px" wrap>
                      {a.appearances.map((ap, j) => {
                        const parts: string[] = [ap.role];
                        if (ap.phase) parts.push(ap.phase);
                        if (ap.functionSelector) parts.push(ap.functionSelector.slice(0, 10));
                        if (ap.calldataIndex != null) parts.push(`arg${ap.calldataIndex}`);
                        return (
                          <RoleBadge
                            key={j}
                            color={roleColor(ap.role)}
                            title={[
                              `role: ${ap.role}`,
                              ap.phase ? `phase: ${ap.phase}` : null,
                              ap.callIndex != null ? `call #${ap.callIndex}` : null,
                              ap.calldataIndex != null ? `arg #${ap.calldataIndex}` : null,
                              ap.functionSelector ? `selector: ${ap.functionSelector}` : null,
                            ].filter(Boolean).join(" | ")}
                            style={{ borderColor: ap.phase ? phaseColor(ap.phase) : undefined }}
                          >
                            {parts.join(" · ")}
                          </RoleBadge>
                        );
                      })}
                    </Flex>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrapper>
      </TableCard>
    </>
  );
}
